import React, { createContext, useState, useContext, useEffect } from 'react';

// Datos de las mieles
const honeyTypes = [
  {
    id: 'citricos',
    name: 'Miel de Cítricos',
    image: '/assets/images/miel_naranja.jpeg',
    description: 'Miel de color claro, generalmente por debajo de 45mm Pfund, correspondiendo a una denominación comercial que va del blanco agua al ámbar extra claro. Posee un aroma característico, muy singular debido a la presencia de antranilato de metilo, que recuerda al de la flor de los Citrus.',
    characteristics: [
      'Color claro, del blanco agua al ámbar extra claro',
      'Aroma característico que recuerda a la flor de los Citrus',
      'Gusto muy diferente al resto de las mieles claras debido a su acidez muy marcada',
      'Cristalización no muy rápida en cristales finos',
      'Requiere mínimo 10-20% de granos de polen de citrus para su denominación'
    ]
  },
  {
    id: 'eucalipto',
    name: 'Miel de Eucalipto',
    image: '/assets/images/miel_eucalipto.jpeg',
    description: 'Miel de color ámbar claro a ámbar, comprendido entre 66 y 89 mm Pfund. También tiene brillos verdosos, apreciables sobre todo en la superficie. Posee un aroma muy intenso y extraordinariamente persistente, que recuerda de forma muy clara al de la madera mojada.',
    characteristics: [
      'Color ámbar claro a ámbar (66-89 mm Pfund)',
      'Brillos verdosos apreciables en la superficie',
      'Aroma muy intenso y persistente a madera mojada',
      'Sabor dulce con ligeras notas ácidas',
      'Tendencia media a la cristalización con cristales finos',
      'Requiere mínimo 70% de granos de polen de eucalipto para su denominación'
    ]
  },
  {
    id: 'treboles',
    name: 'Miel de Tréboles',
    image: '/assets/images/miel_flores_silvestres.jpeg',
    description: 'Miel de pradera con color que va del blanco al ámbar extra claro, comprendido entre 16 y 50 mm Pfund. Tiene un aroma floral muy tenue y poco persistente, con un sabor dulce intenso. Cristaliza rápidamente en cristales blancos y finos.',
    characteristics: [
      'Color del blanco al ámbar extra claro (16-50 mm Pfund)',
      'Aroma floral muy tenue y poco persistente',
      'Sabor dulce intenso',
      'Cristalización rápida en cristales blancos y finos',
      'Contiene pólenes de diferentes géneros como Melilotus, Medicago y Trifolium',
      'Requiere mínimo 45% de pólenes de la tribu Trifolieae para su denominación'
    ]
  }
];

// Creación del contexto
const GameContext = createContext();

// Hook personalizado para usar el contexto
export const useGame = () => useContext(GameContext);

// Proveedor del contexto
export const GameProvider = ({ children }) => {
  // Estado del juego
  const [gameState, setGameState] = useState(() => ({
    started: false,
    finished: false,
    score: 0,
    maxScore: 300,
    selections: [],
    correctAnswers: [],
    hintsUsed: 0,
    timeElapsed: 0,
    playerName: '',
    competitiveMode: false,
    leaderboard: [],
    customHoneyOrder: null // Nuevo estado para el orden personalizado
  }));

  // Cargar datos del almacenamiento local al iniciar
  useEffect(() => {
    const savedLeaderboard = localStorage.getItem('mielMasterLeaderboard');
    if (savedLeaderboard) {
      try {
        const parsedLeaderboard = JSON.parse(savedLeaderboard);
        setGameState(prev => ({ ...prev, leaderboard: parsedLeaderboard }));
      } catch (error) {
        console.error('Error parsing leaderboard data:', error);
      }
    }

    // Cargar configuración personalizada de mieles
    const savedCustomOrder = localStorage.getItem('mielMasterCustomOrder');
    if (savedCustomOrder) {
      try {
        const parsedCustomOrder = JSON.parse(savedCustomOrder);
        setGameState(prev => ({ ...prev, customHoneyOrder: parsedCustomOrder }));
      } catch (error) {
        console.error('Error parsing custom honey order data:', error);
      }
    }
  }, []);

  // Guardar datos en el almacenamiento local cuando cambia el leaderboard
  useEffect(() => {
    if (gameState.leaderboard.length > 0) {
      localStorage.setItem('mielMasterLeaderboard', JSON.stringify(gameState.leaderboard));
    }
  }, [gameState.leaderboard]);

  // Guardar el orden personalizado cuando cambia
  useEffect(() => {
    if (gameState.customHoneyOrder) {
      localStorage.setItem('mielMasterCustomOrder', JSON.stringify(gameState.customHoneyOrder));
    }
  }, [gameState.customHoneyOrder]);

  // Iniciar un nuevo juego
  const startGame = (playerName = '', competitiveMode = false) => {
    // Obtener la configuración personalizada más reciente
    let customOrder = null;
    
    try {
      // Intentar obtener la configuración del localStorage directamente
      const savedOrder = localStorage.getItem('mielMasterCustomOrder');
      if (savedOrder) {
        customOrder = JSON.parse(savedOrder);
        console.log('Usando configuración personalizada de mieles:', customOrder);
      }
    } catch (error) {
      console.error('Error al cargar la configuración personalizada:', error);
    }
    
    // Si no hay configuración personalizada en localStorage, intentar usar la del estado
    if (!customOrder && gameState.customHoneyOrder) {
      customOrder = gameState.customHoneyOrder;
      console.log('Usando configuración personalizada del estado:', customOrder);
    }
    
    // Determinar el orden de las mieles para el juego
    let honeyOrder;
    
    if (customOrder && customOrder.length === 3) {
      // Si hay un orden personalizado válido, usarlo directamente
      honeyOrder = customOrder;
    } else {
      // Si no hay orden personalizado válido, crear uno aleatorio
      honeyOrder = [...honeyTypes].sort(() => Math.random() - 0.5).map(honey => honey.id);
      console.log('Usando orden aleatorio de mieles:', honeyOrder);
    }
    
    setGameState({
      started: true,
      finished: false,
      score: 300, 
      selections: [],
      correctAnswers: honeyOrder,
      hintsUsed: 0,
      timeElapsed: 0,
      playerName: playerName,
      competitiveMode: competitiveMode,
      leaderboard: gameState.leaderboard,
      customHoneyOrder: customOrder
    });
  };

  // Seleccionar una miel
  const selectHoney = (position, honeyId) => {
    const newSelections = [...gameState.selections];
    newSelections[position] = honeyId;
    
    setGameState(prev => ({
      ...prev,
      selections: newSelections
    }));
  };

  // Usar una pista
  const useHint = () => {
    // Restar puntos por usar una pista (50 puntos)
    const newScore = Math.max(0, gameState.score - 50);
    
    setGameState(prev => ({
      ...prev,
      score: newScore,
      hintsUsed: prev.hintsUsed + 1
    }));
  };

  // Finalizar el juego
  const finishGame = () => {
    // Calcular puntuación final
    let finalScore = typeof gameState.score === 'number' ? gameState.score : 300;
    
    // Verificar respuestas correctas
    let correctCount = 0;
    gameState.selections.forEach((selection, index) => {
      if (selection === gameState.correctAnswers[index]) {
        correctCount++;
      } else {
        // Restar puntos por respuestas incorrectas (100 puntos por cada una)
        finalScore = Math.max(0, finalScore - 100);
      }
    });
    
    // Asegurar que finalScore sea un número válido
    finalScore = isNaN(finalScore) ? 0 : Math.floor(finalScore);
    
    // Actualizar leaderboard si está en modo competitivo
    let updatedLeaderboard = [...(gameState.leaderboard || [])];
    if (gameState.competitiveMode && gameState.playerName) {
      updatedLeaderboard.push({
        name: gameState.playerName,
        score: finalScore,
        correctAnswers: correctCount,
        date: new Date().toISOString()
      });
      
      // Ordenar por puntuación (mayor a menor)
      updatedLeaderboard.sort((a, b) => b.score - a.score);
      
      // Limitar a los 10 mejores
      updatedLeaderboard = updatedLeaderboard.slice(0, 10);
    }
    
    setGameState(prev => ({
      ...prev,
      finished: true,
      score: finalScore,
      leaderboard: updatedLeaderboard
    }));
    
    return {
      score: finalScore,
      correctCount: correctCount,
      selections: gameState.selections || [],
      correctAnswers: gameState.correctAnswers || []
    };
  };

  // Reiniciar el juego
  const resetGame = () => {
    setGameState(prev => ({
      ...prev,
      started: false,
      finished: false,
      score: 300, 
      selections: [],
      hintsUsed: 0,
      timeElapsed: 0
    }));
  };

  // Función para establecer un orden personalizado de mieles
  const setCustomHoneyOrder = (honeyOrder) => {
    // Guardar directamente en localStorage para asegurar la persistencia
    localStorage.setItem('mielMasterCustomOrder', JSON.stringify(honeyOrder));
    
    // Actualizar el estado
    setGameState(prev => ({
      ...prev,
      customHoneyOrder: honeyOrder
    }));
    
    console.log('Orden de mieles guardado:', honeyOrder);
  };

  // Función para obtener el orden personalizado actual
  const getCustomHoneyOrder = () => {
    // Intentar obtener del estado primero
    if (gameState.customHoneyOrder) {
      return gameState.customHoneyOrder;
    }
    
    // Si no está en el estado, intentar obtener de localStorage
    try {
      const savedOrder = localStorage.getItem('mielMasterCustomOrder');
      if (savedOrder) {
        const parsedOrder = JSON.parse(savedOrder);
        // Actualizar el estado con lo que encontramos en localStorage
        setGameState(prev => ({
          ...prev,
          customHoneyOrder: parsedOrder
        }));
        return parsedOrder;
      }
    } catch (error) {
      console.error('Error al recuperar el orden personalizado:', error);
    }
    
    return null;
  };

  // Valor del contexto
  const value = {
    gameState,
    honeyTypes,
    startGame,
    selectHoney,
    useHint,
    finishGame,
    resetGame,
    setCustomHoneyOrder,
    getCustomHoneyOrder
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export default GameContext;
